import { CloseButton } from '@components/utility/buttons';
import { ActivationOverviewContext, useExtendedWarrantyContext } from '@extendedWarranty/contexts';
import { EditSerialNumbersSchema } from '@extendedWarranty/schemas';
import { ISerialSubmission } from '@extendedWarranty/types/extendedWarranty';
import { areDuplicatesFixed } from '@extendedWarranty/utils/extendedWarranty';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Backdrop, Button, Dialog, DialogProps, IconButton, TextField, Typography } from '@mui/material';
import { isMac } from '@utils/common';
import { useFormik } from 'formik';
import { useContext, useMemo } from 'react';

type EditInstallationDialogProps = {
    editables: ISerialSubmission[];
    installationId: string;
};

const EditInstallationDialog = ({
    editables,
    installationId,
    ...props
}: Omit<DialogProps, 'children'> & EditInstallationDialogProps) => {
    const { installations } = useExtendedWarrantyContext();
    const allSerialNumbers = useMemo(
        () =>
            Object.keys(installations).reduce(
                (acc, curr) => [
                    ...acc,
                    ...installations[curr].serial_numbers.filter(
                        (sn) =>
                            curr !== installationId ||
                            (curr === installationId &&
                                !sn.ValidationMessageError &&
                                (!sn.isDuplicate || areDuplicatesFixed(installations, sn)))
                    ),
                ],
                [] as ISerialSubmission[]
            ),
        [installationId, installations]
    );

    const { handleEditConfirm } = useContext(ActivationOverviewContext);
    const { values, errors, handleChange, handleBlur, isValid, setFieldValue, handleSubmit } = useFormik({
        initialValues: editables
            .map((serialNumber) => ({
                [serialNumber.id as string]: serialNumber.edit_value || (serialNumber.SerialNumber as string),
            }))
            .reduce((acc, curr) => {
                return { ...acc, ...curr };
            }, {}),
        validationSchema: EditSerialNumbersSchema({
            initialValues: editables
                .map((serialNumber) => ({ [serialNumber.id as string]: serialNumber }))
                .reduce((acc, curr) => {
                    return { ...acc, ...curr };
                }, {}),
            allSerialNumbers,
        }),
        onSubmit: (values) => {
            handleEditConfirm(installationId, values);
            props.onClose?.({}, 'backdropClick');
        },
        enableReinitialize: true,
        validateOnMount: true,
    });

    return (
        <>
            {isMac ? <Backdrop open={props.open} sx={{ pointerEvents: 'none', zIndex: 1 }}></Backdrop> : null}
            <Dialog
                onClose={props.onClose}
                classes={{
                    root: '!absolute pointer-events-auto',
                    paper: 'w-full !m-0 h-full !max-h-full overflow-[unset]',
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            pointerEvents: 'none',
                        },
                    },

                    paper: {
                        sx: {
                            overflow: 'unset',
                        },
                    },
                }}
                disableEscapeKeyDown
                {...props}
            >
                <CloseButton className="flex" onClick={() => props.onClose?.({}, 'backdropClick')} />
                <form onSubmit={handleSubmit} className="pt-12 h-full relative flex flex-col">
                    <div className="px-4 lg:px-14">
                        <Typography variant="h5" fontWeight={700}>
                            Edit Incorrect Serial Numbers
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            Fill the containers with the correct serial numbers.
                        </Typography>
                    </div>
                    <div className="py-7 flex flex-col gap-7 overflow-auto px-4 lg:px-14 h-full">
                        {Object.keys(values).map((serialNumber, index) => (
                            <div key={`edit-${serialNumber}`} className="w-full flex ">
                                <div className="w-full lg:w-[530px]">
                                    <TextField
                                        label={`Serial Number ${index + 1}`}
                                        name={serialNumber}
                                        onChange={handleChange}
                                        value={values[serialNumber]}
                                        error={!!errors[serialNumber]}
                                        helperText={errors[serialNumber]}
                                        onBlur={handleBlur}
                                        fullWidth
                                    />
                                </div>
                                <div>
                                    <IconButton
                                        className="!mt-2 !text-jasper"
                                        onClick={() => setFieldValue(serialNumber, undefined)}
                                    >
                                        <DeleteOutlinedIcon />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="bg-white py-5 px-4 lg:p-0 sticky lg:!absolute bottom-0 lg:bottom-10 right-0 lg:right-10">
                        <Button
                            disabled={!isValid}
                            className="w-full lg:w-min"
                            color="victron-blue"
                            startIcon={<CheckBoxIcon />}
                            type="submit"
                        >
                            CONFIRM
                        </Button>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export { EditInstallationDialog };
